<script setup lang="ts">
import { computed } from "vue";
import {
    isCurrentUserById,
    isGuest,
    isLoggedIn,
    isVerified,
    mustBeLogin,
    mustBeVerified,
} from "@/Utils/helpers";
import TheLayoutApp from "@/Layouts/TheLayoutApp.vue";
import AppPage from "@/Components/Shared/page/AppPage.vue";
import AppContainer from "@/Components/Shared/container/AppContainer.vue";
import AppGrid from "@/Components/Shared/grid/AppGrid.vue";
import AppColumn from "@/Components/Shared/grid/AppColumn.vue";
import AppHeader from "@/Components/Shared/header/AppHeader.vue";
import AppTitle from "@/Components/Shared/title/AppTitle.vue";
import AppArticle from "@/Components/Shared/article/AppArticle.vue";
import AppImageWrapper from "@/Components/Shared/image/AppImageWrapper.vue";
import AppImage from "@/Components/Shared/image/AppImage.vue";
import AppText from "@/Components/Shared/text/AppText.vue";
import AppButtonGroup from "@/Components/Shared/button/AppButtonGroup.vue";
import AppButton from "@/Components/Shared/button/AppButton.vue";
import AppConfirmationDelete from "@/Components/Shared/dialog/AppConfirmationDelete.vue";
import AppUserAvatar from "@/Components/Shared/user/AppUserAvatar.vue";
import AppList from "@/Components/Shared/list/AppList.vue";
import AppListItem from "@/Components/Shared/list/AppListItem.vue";
import AppLink from "@/Components/Shared/link/AppLink.vue";
import AppGrade from "@/Components/App/grades/AppGrade.vue";
import AppAlert from "@/Components/Shared/alert/AppAlert.vue";

import { useModal } from "@/Utils/use-modal";
import AppProductCard from "@/Components/Shared/product/AppProductCard.vue";
import AppUserInfoCard from "@/Components/App/users/AppUserInfoCard.vue";
import TheFlagForm from "@/Components/App/flags/TheFlagForm.vue";
import TheReviewUpdateForm from "@/Components/App/review-updates/TheReviewUpdateForm.vue";
import AppReviewUpdateCard from "@/Components/App/review-updates/AppReviewUpdateCard.vue";
import AppComments from "@/Components/App/comments/AppComments.vue";
import AppLikeButtonReview from "@/Components/App/likes/AppLikeButtonReview.vue";
import AppCollectionGrid from "@/Components/App/collections/AppCollectionGrid.vue";

type Props = {
    review: object;
    schema?: string;
    reviewUpdates: object;
    comments: object;
    collections: object;
};

const {
    review,
    schema = undefined,
    reviewUpdates,
    comments,
    collections = undefined,
} = defineProps<Props>();

// computed
const authorName = computed(() => {
    return review.data.author.name
        ? review.data.author.name
        : `@${review.data.author.username}`;
});
const isOwner = computed(() => {
    return isLoggedIn ? isCurrentUserById(review.data.author.id) : false;
});

const latestRating = computed(() => {
    return review.data.latest_update
        ? review.data.latest_update.rating
        : review.data.rating;
});

const productSpecs = computed(() => {
    if (!review.data.product.specs) return {};

    return review.data.product.specs.reduce((acc: any, current: any) => {
        const specName = current.spec.name;

        if (!acc[specName]) {
            acc[specName] = [current.spec_value || current.value];
        } else {
            acc[specName].push(current.spec_value || current.value);
        }
        return acc;
    }, {});
});

// methods
const { openModal } = useModal();

const addUpdate = () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    openModal({
        component: TheReviewUpdateForm,
        title: "Update toevoegen",
        props: {
            reviewApiPath: review.data._path,
            latestRating,
        },
    });
};

const flag = () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    openModal({
        component: TheFlagForm,
        title: "Review rapporteren",
        props: {
            itemId: review.data.id,
            apiPath: review.data._path,
            type: "review",
        },
    });
};

const breadcrumbs = [];

if (review.data.product.category) {
    if (review.data.product.category.theme) {
        breadcrumbs.push({
            label: review.data.product.category.theme.name,
            path: review.data.product.category.theme.path,
        });
    }

    breadcrumbs.push({
        label: review.data.product.category.name,
        path: review.data.product.category.path,
    });
}

breadcrumbs.push({
    label: review.data.product.name,
    path: review.data.product.path,
});
</script>

<template>
    <TheLayoutApp
        :title="`${authorName} - ${review.data.product.name}`"
        :description="review.data.excerpt"
        :schema="schema"
        :breadcrumbs="breadcrumbs"
    >
        <AppPage size="small">
            <AppContainer>
                <AppGrid>
                    <AppColumn class="md:col-span-7 lg:col-span-8">
                        <AppAlert
                            v-if="review.data.latest_moderation"
                            class="mb-8"
                            size="small"
                        >
                            Deze review is onzichtbaar gemaakt door een
                            moderator
                            <template
                                v-if="review.data.latest_moderation.reason"
                            >
                                met als reden: "{{
                                    review.data.latest_moderation.reason
                                }}"
                            </template>
                        </AppAlert>

                        <section class="mb-4">
                            <div class="flex items-start gap-6 mb-6">
                                <div class="relative flex">
                                    <AppGrade
                                        :grade="review.data.rating"
                                        size="xlarge"
                                        :class="
                                            review.data.latest_update
                                                ? 'opacity-40'
                                                : ''
                                        "
                                    />
                                    <AppGrade
                                        v-if="review.data.latest_update"
                                        :grade="
                                            review.data.latest_update.rating
                                        "
                                        class="absolute -bottom-1 -right-1"
                                    />
                                </div>

                                <AppHeader
                                    size="none"
                                    gap="small"
                                >
                                    <template v-if="review.data.title">
                                        <AppTitle>
                                            {{ review.data.title }}
                                        </AppTitle>

                                        <AppTitle
                                            as="h2"
                                            size="small"
                                        >
                                            {{ review.data.product.name }}
                                            review
                                            <span
                                                v-if="
                                                    review.data.status ===
                                                    'draft'
                                                "
                                                class="font-normal text-gray-400"
                                            >
                                                &mdash; concept
                                            </span>
                                        </AppTitle>
                                    </template>
                                    <AppTitle v-else>
                                        {{ review.data.product.name }} review
                                        <span
                                            v-if="
                                                review.data.status === 'draft'
                                            "
                                            class="font-normal text-gray-400"
                                        >
                                            &mdash; concept
                                        </span>
                                    </AppTitle>

                                    <AppUserAvatar
                                        :user="review.data.author"
                                        show-name
                                        avatar-size="xsmall"
                                        color="light"
                                    />
                                </AppHeader>
                            </div>

                            <AppText
                                v-if="review.data.information"
                                color="gray"
                                size="small"
                                class="mb-2"
                            >
                                Geschreven voor: {{ review.data.information }}
                            </AppText>

                            <AppArticle
                                v-if="review.data.content"
                                size="large"
                                v-html="review.data.content"
                            />

                            <div
                                v-if="
                                    review.data.metrics &&
                                    review.data.metrics.data &&
                                    review.data.metrics.data.length
                                "
                                class="flex flex-col gap-6 sm:gap-12 mt-8 border-t py-8"
                            >
                                <section
                                    v-for="metric in review.data.metrics.data"
                                >
                                    <AppHeader
                                        v-if="metric.name"
                                        variant="inline"
                                        class="gap-4"
                                    >
                                        <AppGrade :grade="metric.rating" />
                                        <AppTitle as="h3">
                                            {{ metric.name }}
                                        </AppTitle>
                                    </AppHeader>
                                    <AppArticle
                                        v-if="metric.content"
                                        size="large"
                                        v-html="metric.content"
                                    />
                                </section>
                            </div>

                            <AppGrid
                                v-if="
                                    (review.data.cons &&
                                        review.data.cons.length) ||
                                    (review.data.pros &&
                                        review.data.pros.length)
                                "
                                class="mt-8 border border-slate-200 p-4 sm:p-6 rounded"
                            >
                                <AppColumn
                                    v-if="
                                        review.data.pros &&
                                        review.data.pros.length
                                    "
                                    class="md:col-span-6"
                                >
                                    <section>
                                        <AppHeader>
                                            <AppTitle
                                                as="h4"
                                                size="xsmall"
                                                color="gray"
                                            >
                                                Pluspunten
                                            </AppTitle>
                                        </AppHeader>
                                        <AppList gap="large">
                                            <AppListItem
                                                v-for="pro in review.data.pros"
                                                icon="check"
                                                icon-size="small"
                                                icon-color="gray"
                                            >
                                                {{ pro }}
                                            </AppListItem>
                                        </AppList>
                                    </section>
                                </AppColumn>

                                <AppColumn
                                    v-if="
                                        review.data.cons &&
                                        review.data.cons.length
                                    "
                                    class="md:col-span-6"
                                >
                                    <section>
                                        <AppHeader>
                                            <AppTitle
                                                as="h4"
                                                size="xsmall"
                                                color="gray"
                                            >
                                                Minpunten
                                            </AppTitle>
                                        </AppHeader>
                                        <AppList gap="large">
                                            <AppListItem
                                                v-for="con in review.data.cons"
                                                icon="cross"
                                                icon-size="small"
                                                icon-color="gray"
                                            >
                                                {{ con }}
                                            </AppListItem>
                                        </AppList>
                                    </section>
                                </AppColumn>
                            </AppGrid>

                            <AppImageWrapper
                                v-if="review.data.image_path"
                                class="mt-4 mx-auto"
                                size="xlarge"
                            >
                                <AppImage
                                    class="rounded-sm"
                                    :src="review.data.image_path"
                                />
                            </AppImageWrapper>

                            <div class="flex gap-4 mt-8">
                                <AppText
                                    color="gray"
                                    size="small"
                                    icon="calendar"
                                >
                                    {{ review.data.created }}
                                    <span
                                        v-if="
                                            review.data.updated &&
                                            review.data.created !==
                                                review.data.updated
                                        "
                                    >
                                        — laatst aangepast:
                                        {{ review.data.updated }}
                                    </span>
                                </AppText>
                            </div>
                        </section>

                        <AppButtonGroup class="mb-6">
                            <AppLikeButtonReview
                                :disabled="isOwner"
                                :review-id="review.data.id"
                                :api-path="review.data._path"
                                :likes-count="review.data.likes_count"
                                :auth-has-liked="review.data.auth_has_liked"
                            />

                            <template v-if="isOwner">
                                <AppLink
                                    variant="buttonXSmall"
                                    :href="`${review.data._path}/edit`"
                                >
                                    Wijzig
                                </AppLink>
                                <AppButton
                                    size="xsmall"
                                    @click="addUpdate"
                                >
                                    Update toevoegen
                                </AppButton>
                                <AppConfirmationDelete
                                    :action="review.data._path"
                                    button-size="xsmall"
                                    :redirect="review.data.author.path"
                                    confirm-text="Weet je zeker dat je deze review volledig wilt verwijderen? Hiermee verlies je: punten, je toegevoegde afbeeldingen, likes en de geplaatst reacties erop."
                                />
                            </template>
                            <template v-else>
                                <AppButton
                                    size="xsmall"
                                    @click="flag"
                                >
                                    Rapporteren
                                </AppButton>
                            </template>
                        </AppButtonGroup>

                        <section
                            v-if="
                                reviewUpdates.data && reviewUpdates.data.length
                            "
                            class="mb-6"
                        >
                            <AppHeader>
                                <AppTitle
                                    as="h3"
                                    size="small"
                                >
                                    Updates
                                </AppTitle>
                            </AppHeader>

                            <div class="flex flex-col gap-2">
                                <AppReviewUpdateCard
                                    v-for="reviewUpdate in reviewUpdates.data"
                                    :review-update="reviewUpdate"
                                    :review-user-id="review.data.author.id"
                                    :review-api-path="review.data._path"
                                    :latest-rating="latestRating"
                                    :can-edit="
                                        review.data.latest_update &&
                                        review.data.latest_update.id &&
                                        reviewUpdate.id ===
                                            review.data.latest_update.id
                                    "
                                />
                            </div>
                        </section>
                        <AppAlert
                            v-else-if="isOwner"
                            class="mt-4 mb-4"
                            variant="default"
                        >
                            Nieuwe inzichten 💡 of gewoon nog steeds helemaal
                            happy 🥳? Voeg dan een
                            <AppButton
                                color="clean"
                                class="underline"
                                @click="addUpdate"
                                >update</AppButton
                            >
                            toe aan deze review. Zo blijven anderen op de
                            hoogte.
                        </AppAlert>

                        <AppComments
                            :model-api-path="review.data._path"
                            :comments="comments.data ?? []"
                            :disabled="review.data.disable_comments"
                            :model-author-id="review.data.author.id"
                            :count="review.data.comments_count"
                        />
                    </AppColumn>
                    <AppColumn class="md:col-span-5 lg:col-span-4">
                        <div class="flex flex-col gap-6">
                            <AppProductCard
                                :product="review.data.product"
                                variant="bordered"
                            />

                            <section v-if="Object.keys(productSpecs).length">
                                <AppHeader>
                                    <AppTitle
                                        as="h2"
                                        size="small"
                                    >
                                        Specificaties
                                    </AppTitle>
                                </AppHeader>
                                <AppList
                                    class="text-sm"
                                    variant="zebra"
                                >
                                    <AppListItem
                                        v-for="(value, key) in productSpecs"
                                        :variant="['zebra', 'withLabel']"
                                    >
                                        <AppText
                                            as="span"
                                            size="small"
                                        >
                                            {{ key }}:
                                        </AppText>
                                        <AppText size="small">{{
                                            value.join(", ")
                                        }}</AppText>
                                    </AppListItem>
                                </AppList>
                            </section>

                            <AppUserInfoCard :user="review.data.author" />

                            <section
                                v-if="collections && collections.data.length"
                            >
                                <AppHeader>
                                    <AppTitle
                                        as="h2"
                                        size="small"
                                    >
                                        In Collectie(s)
                                    </AppTitle>
                                </AppHeader>
                                <AppCollectionGrid
                                    :collections="collections.data"
                                    variant="double"
                                    size="none"
                                />
                            </section>
                        </div>
                    </AppColumn>
                </AppGrid>
            </AppContainer>
        </AppPage>
    </TheLayoutApp>
</template>
